import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, ChevronRight } from "lucide-react";

const FAQPage = () => {
  const faqCategories = [
    {
      title: "Program Details",
      faqs: [
        {
          question: "What age should my puppy be to join?",
          answer: "We offer two programs based on age. Puppy Foundations is for puppies aged 12-16 weeks (plus 2 weeks flexibility). Puppy Development is for puppies aged 16-20 weeks (plus 2 weeks flexibility)."
        },
        {
          question: "How long is each program?",
          answer: "Each program runs for 4 weeks. Your puppy attends one full day per week, giving them time to absorb and practise skills between sessions. We find this weekly structure is optimal for learning and retention."
        },
        {
          question: "What days do programs run?",
          answer: "Puppy Foundations runs on Thursdays. Puppy Development runs on Fridays. This structured schedule helps us maintain consistent training environments for each age group."
        },
        {
          question: "How many dogs are in each group?",
          answer: "We keep groups small to ensure quality attention. Puppy Foundations has a maximum of 6 per group, and Puppy Development has a maximum of 4 per group."
        },
        {
          question: "What does a typical day look like?",
          answer: "Your puppy is picked up in the morning (8:00-9:00 AM pickup window). The day follows a structured rhythm: Training → Rest → Socialisation → Rest → Training. Activities include calm arrival and settling, engagement training, obedience exercises, structured socialisation, confidence building, environmental exposure, rest periods in crates, and a calm wind-down before transport home. They're returned in the afternoon (3:00-4:00 PM drop-off window)."
        }
      ]
    },
    {
      title: "Our Approach",
      faqs: [
        {
          question: "What are the Three Fundamental Elements?",
          answer: "Our training is built around three fundamental elements: Socialisation (structured and intentional exposure to build confidence), Manners (developing appropriate behaviour in everyday situations), and Obedience (building a strong relationship and communication between puppy and handler). Every aspect of our program reinforces these elements."
        },
        {
          question: "What's your training philosophy?",
          answer: "We focus on calm leadership, clear communication, structured learning environments, consistent expectations, controlled social exposure, and development of emotional regulation. Our goal is to develop calm, confident dogs who are attentive to their handler, socially appropriate, and easy to live with in family environments."
        },
        {
          question: "Why do you emphasise calm behaviour?",
          answer: "Calm behaviour is reinforced more than excitement. Your puppy is encouraged to learn how to relax and settle. An over-excited puppy can struggle to learn, so we create environments that promote focus and emotional balance. Rest periods help your puppy process learning and prevent overstimulation."
        },
        {
          question: "What will my puppy learn?",
          answer: "Depending on the program, your puppy will learn: name recognition and engagement, recall foundations, lead skills, polite greetings, impulse control, confidence building, calm crate behaviour, structured socialisation, environmental exposure, and learning to settle calmly."
        }
      ]
    },
    {
      title: "Enrollment & Payment",
      faqs: [
        {
          question: "How much does it cost?",
          answer: "Each 4-week program costs $440, which works out to $110 per session. This includes home pickup and drop-off within our service area, all training materials, weekly report cards with scores and feedback, homework assignments, and a graduation certificate upon completion."
        },
        {
          question: "How do I enroll my puppy?",
          answer: "First, create an account and add your puppy's details. Then view available program intakes and select one that suits your schedule. Complete the payment through PayPal to secure your spot. Spaces are limited, so we recommend booking early."
        },
        {
          question: "When is payment due?",
          answer: "Full payment is required upfront when you enroll to secure your puppy's spot in the program. We accept payment via PayPal."
        },
        {
          question: "What if the intake I want is full?",
          answer: "You can join our waitlist for your preferred program. When spots become available or new intakes are created, we'll notify waitlisted clients both by email and through the app. You can also be manually contacted when appropriate intakes open up."
        },
        {
          question: "What's your cancellation policy?",
          answer: "Please contact us as soon as possible if you need to cancel. Cancellations made more than 7 days before the start date may be eligible for a transfer to a future intake. We assess each situation individually."
        }
      ]
    },
    {
      title: "Transport & Logistics",
      faqs: [
        {
          question: "Do you pick up and drop off my puppy?",
          answer: "Yes! Home pickup and drop-off is included in the program fee within our service area (Sunshine Coast Hinterland). We'll confirm your address is within our service area when you register your puppy."
        },
        {
          question: "How is my puppy transported?",
          answer: "Your puppy is transported in an individual crate in our vehicle. This ensures they arrive calm and settled, ready for their training day. Each puppy has their own space, reducing stress and preventing over-excitement. Safe travel in individual crates is part of our prevention of overstimulation approach."
        },
        {
          question: "What time is pickup and drop-off?",
          answer: "Morning pickups occur between 8:00-9:00 AM. Afternoon drop-offs occur between 3:00-4:00 PM. You'll receive your specific pickup time based on your location in the route."
        },
        {
          question: "What if I'm outside your service area?",
          answer: "Please contact us to discuss options. In some cases, we can arrange pickup/drop-off at a meeting point, or you may choose to transport your puppy to our location yourself."
        }
      ]
    },
    {
      title: "Progress & Communication",
      faqs: [
        {
          question: "How do I track my puppy's progress?",
          answer: "You'll receive detailed weekly report cards after each session. These include numerical scores (1-10) for key skills: socialisation, manners, obedience, recall, lead behaviour, impulse control, confidence, and settling. You'll also receive trainer comments, development summaries, highlights, and areas to focus on at home."
        },
        {
          question: "Can I see my puppy's progress over time?",
          answer: "Yes! Your dashboard shows progress tracking with visual graphs so you can see how your puppy has developed across all skill areas throughout the program. You can watch them grow from week to week."
        },
        {
          question: "Will I get homework?",
          answer: "Yes! You'll receive weekly homework assignments with specific exercises to practise at home. These include the focus area, step-by-step exercises, tips, and recommended daily practice time (usually just 5 minutes). Short, daily training sessions are more effective than longer, less frequent sessions."
        },
        {
          question: "What happens when my puppy graduates?",
          answer: "Upon completing all 4 sessions, your puppy receives a graduation certificate that you can download and print. You'll also get a shareable social media graphic to celebrate their achievement, a summary of their final scores across all skill areas, and a record of their progress throughout the program."
        }
      ]
    },
    {
      title: "Requirements & Safety",
      faqs: [
        {
          question: "What vaccinations does my puppy need?",
          answer: "Your puppy should be up to date with their age-appropriate vaccinations. We require at least the first round of vaccinations before attendance. Please enter your puppy's vaccination status when you register. If you're unsure, consult your vet."
        },
        {
          question: "Is it safe for my young puppy to socialise?",
          answer: "Yes, when done correctly! Our controlled environment ensures your puppy only interacts with other vaccinated dogs of similar age. Early socialisation (before 16 weeks) is critical for puppy development, and our structured approach minimises risks while maximising benefits."
        },
        {
          question: "What if my puppy is nervous or reactive?",
          answer: "Our programs are designed to build confidence in a calm, controlled environment. We carefully manage group dynamics and can adjust our approach for nervous puppies. Please note your puppy's temperament when registering so we can best support them."
        },
        {
          question: "What if my puppy gets sick or injured?",
          answer: "We have first aid training and protocols in place. In an emergency, we would contact you immediately and transport your puppy to a vet if needed. Please ensure your contact details are always up to date."
        }
      ]
    },
    {
      title: "Breeders & Referrals",
      faqs: [
        {
          question: "I'm a breeder. Can I refer my puppy buyers?",
          answer: "Absolutely! We have a breeder referral system. You can submit your clients' details through our breeder referral form, and we'll send them an invitation to register. This helps ensure puppies get a great start to their training journey."
        },
        {
          question: "How do referrals work?",
          answer: "When you enroll, you can tell us how you heard about The Pack Standard. This helps us understand where our families come from. If you were referred by a friend, breeder, vet, or other source, let us know!"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-off-white" data-testid="faq-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-stone hover:text-olive transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <Link to="/" className="flex flex-col items-end">
            <span className="font-heading text-xl italic text-charcoal">The Pack Standard</span>
            <span className="text-olive text-xs tracking-wider">PUPPY ACADEMY</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-stone text-lg max-w-2xl mx-auto">
            Everything you need to know about our puppy programs.
          </p>
        </motion.div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="card-premium"
              data-testid={`faq-category-${categoryIndex}`}
            >
              <h2 className="font-heading text-2xl text-charcoal mb-6">{category.title}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {category.faqs.map((faq, faqIndex) => (
                  <AccordionItem
                    key={`faq-${category.title}-${faqIndex}`}
                    value={`${categoryIndex}-${faqIndex}`}
                    className="border border-olive/10 rounded-lg px-4"
                    data-testid={`faq-${categoryIndex}-${faqIndex}`}
                  >
                    <AccordionTrigger className="text-left font-medium text-charcoal hover:text-olive py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-stone leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-16 text-center bg-sand rounded-2xl p-12"
        >
          <h2 className="font-heading text-3xl text-charcoal mb-4">
            Still Have Questions?
          </h2>
          <p className="text-stone mb-8 max-w-xl mx-auto">
            We'd love to hear from you. Get in touch and we'll get back to you as soon as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:thepackstandard@gmail.com">
              <Button className="btn-primary">
                Contact Us
              </Button>
            </a>
            <Link to="/programs">
              <Button className="btn-outline">
                View Programs
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default FAQPage;
